


import { useState } from 'react';
import { useAppStore } from '@/modules/core/appstore/services/store';

export default function RequestAppModal() {
  const { showRequestModal, setShowRequestModal, addRequest, selectedApp } = useAppStore();
  const [formData, setFormData] = useState({
    appName: '',
    description: '',
    category: 'Productivity',
    businessJustification: '',
    expectedUsers: '',
    priority: 'Medium',
    requesterName: '',
    requesterEmail: '',
    department: '',
    attachments: null as File[] | null
  });

  // Update form data when selectedApp changes
  useState(() => {
    if (selectedApp && showRequestModal) {
      setFormData(prev => ({
        ...prev,
        appName: selectedApp.name,
        description: selectedApp.description,
        category: selectedApp.category
      }));
    }
  });

  if (!showRequestModal) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addRequest(formData);
    setFormData({
      appName: '',
      description: '',
      category: 'Productivity',
      businessJustification: '',
      expectedUsers: '',
      priority: 'Medium',
      requesterName: '',
      requesterEmail: '',
      department: '',
      attachments: null
    });
  };

  const handleClose = () => {
    setShowRequestModal(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setFormData({ ...formData, attachments: Array.from(files) });
    }
  };

  const isInstallRequest = selectedApp && selectedApp.status === 'Not Installed';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {isInstallRequest ? `Request to Install ${selectedApp.name}` : 'Request New App'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <p className="text-gray-600 mt-2">
            {isInstallRequest 
              ? 'Submit a request to install this application with proper justification'
              : 'Submit a request for a new application to be added to the store'
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Name *
              </label>
              <input
                type="text"
                required
                value={formData.appName}
                onChange={(e) => setFormData({ ...formData, appName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter the name of the app"
                readOnly={isInstallRequest}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                disabled={isInstallRequest}
              >
                <option value="Productivity">Productivity</option>
                <option value="Monitoring">Monitoring</option>
                <option value="Governance">Governance</option>
                <option value="Analytics">Analytics</option>
                <option value="Citizen Services">Citizen Services</option>
                <option value="AI Tools">AI Tools</option>
                <option value="Integrations">Integrations</option>
                <option value="Communication">Communication</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isInstallRequest ? 'App Description' : 'Description'} *
            </label>
            <textarea
              required
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={isInstallRequest ? "App description" : "Describe what the app should do and its key features"}
              maxLength={500}
              readOnly={isInstallRequest}
            />
            <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isInstallRequest ? 'Installation Justification' : 'Business Justification'} *
            </label>
            <textarea
              required
              rows={3}
              value={formData.businessJustification}
              onChange={(e) => setFormData({ ...formData, businessJustification: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={isInstallRequest 
                ? "Explain why you need this app installed and how it will benefit your work"
                : "Explain why this app is needed and how it will benefit the organization"
              }
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">{formData.businessJustification.length}/500 characters</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Users *
              </label>
              <input
                type="text"
                required
                value={formData.expectedUsers}
                onChange={(e) => setFormData({ ...formData, expectedUsers: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 50 users, All staff, IT Department"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority *
              </label>
              <select
                required
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name *
              </label>
              <input
                type="text"
                required
                value={formData.requesterName}
                onChange={(e) => setFormData({ ...formData, requesterName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.requesterEmail}
                onChange={(e) => setFormData({ ...formData, requesterEmail: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email address"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department *
            </label>
            <input
              type="text"
              required
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your department or team"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supporting Documents
            </label>
            <div className="w-full px-4 py-3 border-2 border-dashed border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="w-full"
                accept=".pdf,.doc,.docx,.txt,.jpg,.png"
              />
              <p className="text-xs text-gray-500 mt-2">
                Upload supporting documents (PDF, DOC, DOCX, TXT, JPG, PNG - Max 10MB per file)
              </p>
              {formData.attachments && formData.attachments.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-gray-700">Selected files:</p>
                  <ul className="text-xs text-gray-600 mt-1">
                    {formData.attachments.map((file, index) => (
                      <li key={index}>• {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
            >
              {isInstallRequest ? 'Submit Install Request' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
